import React, { useCallback, useState } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";

/**
 * "Mine / All" segmented toggle of the overview pages (explorations,
 * selections, reproductions, substrates, lasers, camera calibrations).
 *
 * A pill-shaped grey track with a white raised thumb that slides to the
 * selected segment (the segmented control of modern car / phone UIs).
 *
 * The chosen scope is sent to the list endpoint as ``?scope=mine|all``; the
 * backend does the owner filtering (``oraclaseapp/list_scope.py``). One
 * choice is shared by every overview and remembered per browser, so the
 * user does not re-select it on each page.
 */
export const LIST_SCOPE_MINE = "mine";
export const LIST_SCOPE_ALL = "all";
const STORAGE_KEY = "overviewListScope";

const OPTIONS = [
  { value: LIST_SCOPE_MINE, label: "Mine", title: "Only the items you created" },
  { value: LIST_SCOPE_ALL, label: "All", title: "Items of every account" },
];

/** Track padding in px: the thumb is inset by this on every side. */
const TRACK_PADDING = 3;
const SLIDE = "220ms cubic-bezier(0.4, 0, 0.2, 1)";

function readStoredScope() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === LIST_SCOPE_MINE ? LIST_SCOPE_MINE : LIST_SCOPE_ALL;
  } catch (_error) {
    return LIST_SCOPE_ALL;
  }
}

/** ``[scope, setScope]`` — state seeded from, and persisted to, localStorage. */
export function useListScope() {
  const [scope, setScopeState] = useState(readStoredScope);
  const setScope = useCallback((next) => {
    if (next !== LIST_SCOPE_MINE && next !== LIST_SCOPE_ALL) return;
    setScopeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (_error) {
      // Private mode / blocked storage: the choice just does not persist.
    }
  }, []);
  return [scope, setScope];
}

/** Append ``scope=<scope>`` to a list URL (with or without existing query). */
export function withListScope(url, scope) {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}scope=${encodeURIComponent(scope)}`;
}

export default function ListScopeToggle({ value, onChange, sx = {} }) {
  const selectedIndex = Math.max(
    0,
    OPTIONS.findIndex((option) => option.value === value)
  );
  return (
    <Box
      role="group"
      aria-label="Show only my items or everyone's"
      sx={{
        position: "relative",
        display: "inline-grid",
        gridTemplateColumns: `repeat(${OPTIONS.length}, 1fr)`,
        p: `${TRACK_PADDING}px`,
        borderRadius: 999,
        bgcolor: "#e3e5ea",
        flexShrink: 0,
        userSelect: "none",
        ...sx,
      }}
    >
      {/* Sliding thumb: one segment wide, moved by whole segment widths. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: `${TRACK_PADDING}px`,
          bottom: `${TRACK_PADDING}px`,
          left: `${TRACK_PADDING}px`,
          width: `calc((100% - ${2 * TRACK_PADDING}px) / ${OPTIONS.length})`,
          borderRadius: 999,
          bgcolor: "#ffffff",
          boxShadow: "0 1px 3px rgba(15, 23, 42, 0.18), 0 1px 1px rgba(15, 23, 42, 0.08)",
          transform: `translateX(${selectedIndex * 100}%)`,
          transition: `transform ${SLIDE}`,
        }}
      />
      {OPTIONS.map((option) => {
        const selected = option.value === value;
        return (
          <Tooltip key={option.value} title={option.title}>
            <ButtonBase
              onClick={() => {
                if (!selected) onChange(option.value);
              }}
              aria-label={option.label}
              aria-pressed={selected}
              disableRipple
              sx={{
                position: "relative",
                zIndex: 1,
                minWidth: 76,
                px: 2,
                py: 0.75,
                borderRadius: 999,
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                lineHeight: 1.2,
                color: selected ? "#1f2937" : "#8a92a0",
                transition: `color ${SLIDE}`,
                "&:hover": { color: selected ? "#1f2937" : "#4b5563" },
                "&.Mui-focusVisible": {
                  outline: "2px solid rgba(76, 126, 255, 0.5)",
                  outlineOffset: -2,
                },
              }}
            >
              {option.label}
            </ButtonBase>
          </Tooltip>
        );
      })}
    </Box>
  );
}
